import { Injectable } from '@angular/core';
import { Subject, Observable, Subscription, BehaviorSubject } from 'rxjs';
import * as uuid from 'uuid';
import { UtilsService } from '../core/utils.service';
import { ThorVoiceService } from './thor-voice.service';
import { ThorWebsocketService } from './thor-websocket.service';
import { environment } from 'environments/environment';

@Injectable({
    providedIn: 'root'
})
export class ThorChatService {
    private sessionId: string;
    private user: any;
    private voiceSubscription: Subscription;
    private wsSubscription: Subscription;
    private latestQuery: string = '';

    private isProcessingSubject = new BehaviorSubject<boolean>(false);
    public isProcessing$: Observable<boolean> = this.isProcessingSubject.asObservable();

    /**
     * GENERIC EVENT BUS: Any module can subscribe to performAction$ 
     * to listen for Thor-triggered frontend events.
     */
    private performActionSubject = new Subject<any>();
    public performAction$: Observable<any> = this.performActionSubject.asObservable();

    constructor(
        private utils: UtilsService,
        private voiceService: ThorVoiceService,
        private wsService: ThorWebsocketService
    ) {
        this.user = this.utils.getLoggedInUser();
        this.sessionId = uuid.v4();
        this.voiceSubscription = this.voiceService.dataReceived$.subscribe(data => {
            this.handleThorResponse(data);
        });
        this.wsSubscription = this.wsService.onMessage$.subscribe(data => {
            this.handleThorResponse(data);
        });
    }

    public getSessionId(): string {
        return this.sessionId;
    }

    public getNewSessionId(): string {
        this.sessionId = uuid.v4();
        return this.sessionId;
    }

    public setLatestQuery(query: string) {
        this.latestQuery = query;
    }

    public getLatestQuery(): string {
        return this.latestQuery;
    }

    public saveThorState(query: string, resultIds: string[]) {
        const state = {
            query: query,
            resultIds: resultIds,
            timestamp: new Date().getTime()
        };
        const encrypted = this.utils.idEncryption(JSON.stringify(state));
        localStorage.setItem('thor_portfolio_state', encrypted.toString());
    }

    public getThorState(): any {
        const encrypted = localStorage.getItem('thor_portfolio_state');
        if (encrypted) {
            try {
                const decrypted = this.utils.idDecryption(encrypted);
                return JSON.parse(decrypted);
            } catch (e) {
                console.error('Thor Service: Failed to decrypt state', e);
                return null;
            }
        }
        return null;
    }

    public clearThorState() {
        this.utils.removeItem('thor_portfolio_state');
    }

    private handleThorResponse(data: any) {
        this.isProcessingSubject.next(false);
        
        // Module Action
        if (data && data.action_response) {
            const action = data.action_response;
            
            // Always dispatch to event bus for downstream module handling
            this.performActionSubject.next(action);

            // Toast Feedback for explicit failures or unrelated queries
            if (action.action_enabled === false) {
                if (action.error_response && action.error_response.trim() !== "") {
                    // LLM/THOR side error
                    this.utils.growlMessages('error', 'THOR is not available. Please try again later.');
                } else {
                    // Unrelated question
                    this.utils.growlMessages('warn', 'This request is beyond my scope. Please try a different request.');
                }
            }
        }
    }

    public sendQuery(query: string) {
        this.isProcessingSubject.next(true);
        const payload = {
            user_query: query,
            model_id: "kronos_thor_v1",
            env_id: environment.production ? "prod" : "dev",
            session_id: this.sessionId,
            action_enabled: true,
            chat_history_enabled: false,
            user_data: {
                user_name: this.user.email,
                user_id: this.user.id.toString(),
                user_email: this.user.email,
                auth_token: this.user.sessionId
            }
        };

        // Commented out for temporary testing
        // Convert to string and send via the LiveKit data channel
        // this.voiceService.sendData(JSON.stringify(payload)).catch(err => {
        //     console.error('Thor Service: Failed to send query', err);
        // });
    }

    public stop() {
        this.voiceService.disconnectRoom();
    }
}